import type { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { Button } from '@design/components/button/Button';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { EllipsisDetector } from '@terminal/components/ellipsis-detector/EllipsisDetector';
import { WatchlistTrigger } from '@terminal/components/watchlist/watchlist-trigger/WatchlistTrigger';
import type { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './EntityHeaderWatchlist.module.scss';

export interface EntityHeaderWatchlistProps {
  entity: Entity;
  logoSize?: number;
  className?: string;
  isFetching?: boolean;
  variant?: 'default' | 'compact';
  trackingSource?: PosthogTrackingSource;
  watchlistButtonTestId?: string;
}

export const EntityHeaderWatchlist = ({
  entity,
  className,
  isFetching,
  variant = 'default',
  logoSize = variant === 'compact' ? 16 : 40,
  trackingSource,
  watchlistButtonTestId,
}: EntityHeaderWatchlistProps) => {
  const { t } = useTranslation('common');
  const { trackWatchlistCreate } = usePosthogEvents();

  const ticker = formatTicker(entity);
  const entityName = entity.name_short || entity.name_full;
  const resolvedWatchlistButtonTestId =
    watchlistButtonTestId ??
    (variant === 'compact'
      ? 'company-catalyst-chart-watchlist-button'
      : 'watchlist-button');

  const handleWatchlistCreated = (watchlistId: string) => {
    if (!trackingSource) {
      return;
    }

    trackWatchlistCreate(trackingSource, watchlistId);
  };

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '50px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  return (
    <div
      data-entity-header-watchlist
      className={clsx(styles.contentWrapper, className, {
        [styles.compact]: variant === 'compact',
      })}
    >
      <div className={styles.ticker}>
        <div className={styles.leftImageContainer}>
          <AssetLogoContainer size={logoSize} entity={entity} />
        </div>
        <div className={styles.titleContainer}>
          <div className={styles.nameRow}>
            <div className={styles.entityName}>
              <EllipsisDetector label={entityName} placement="top">
                <span data-testid={`label${entityName}`}>{entityName}</span>
              </EllipsisDetector>
            </div>
            <WatchlistTrigger
              onWatchlistCreated={
                trackingSource ? handleWatchlistCreated : undefined
              }
              tag={entity.tag}
              dropdownProps={{
                strategy: 'fixed',
                placement: 'top-start',
                inPortal: false,
              }}
              renderTrigger={isInWatchlist => (
                <Button
                  aria-label={
                    isInWatchlist
                      ? t('components.watchlistBtn.watching')
                      : t('components.watchlistBtn.addToWatchlistLabel')
                  }
                  className={clsx(styles.watchlistButton, {
                    [styles.watchlistButtonActive]: isInWatchlist,
                    [styles.compactWatchlistButton]: variant === 'compact',
                  })}
                  data-testid={resolvedWatchlistButtonTestId}
                  data-compact-chart-watchlist-button={
                    variant === 'compact' ? '' : undefined
                  }
                  size="xsmall"
                  variant="tertiary"
                  iconName={isInWatchlist ? 'Checkmark' : 'Add'}
                />
              )}
            />
          </div>
          <span className={styles.tag}>{ticker}</span>
        </div>
      </div>
    </div>
  );
};
